import { useEffect, useRef, useState, type RefObject } from 'react';
import { useTenantAppearance } from '../../contexts/TenantAppearanceContext';
import {
  mapPinGroupKey,
  summarizeMapPinLabels,
  type ApplicationHomeMapPin,
} from '../../utils/application-homes-map';
import { tenantGoogleMapsLoader } from '../../utils/tenantGoogleMapsLoader';

export type ApplicationHomePin = ApplicationHomeMapPin;

interface MapPoint {
  latitude: number | null;
  longitude: number | null;
}

interface ApplicationHomesMapProps {
  homes: ApplicationHomePin[];
  area?: MapPoint[];
  /** Stretch to fill a tall grid column (Apply to Homes side panel). */
  fillHeight?: boolean;
}

interface PlacedPin {
  key: string;
  labels: string[];
  pickLabels: string[];
  position: google.maps.LatLngLiteral;
}

type MapLoadState = 'loading' | 'ready' | 'no-key' | 'failed';

const KINGSTON = { lat: 44.2312, lng: -76.486 };
const PIN_COLOR = '#e22e07';
const LIGHT_MAP_BACKGROUND = '#f3f0ea';
const DARK_MAP_BACKGROUND = '#0f172a';
const MAP_STYLE: google.maps.MapTypeStyle[] = [
  { elementType: 'geometry', stylers: [{ color: '#f3f0ea' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#6d675f' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#f3f0ea' }] },
  { featureType: 'administrative', elementType: 'geometry.stroke', stylers: [{ color: '#d9d3cb' }] },
  { featureType: 'landscape.natural', elementType: 'geometry', stylers: [{ color: '#e7efe4' }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.park', stylers: [{ visibility: 'on' }] },
  { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#dce8d8' }] },
  { featureType: 'poi.park', elementType: 'labels', stylers: [{ visibility: 'off' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
  { featureType: 'road', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#e6e0d6' }] },
  { featureType: 'road.arterial', elementType: 'labels', stylers: [{ visibility: 'simplified' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#d3e2ea' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#7b92a0' }] },
];

const DARK_MAP_STYLE: google.maps.MapTypeStyle[] = [
  { elementType: 'geometry', stylers: [{ color: '#0f172a' }] },
  { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#94a3b8' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#0f172a' }] },
  { featureType: 'administrative', elementType: 'geometry', stylers: [{ color: '#1e293b' }] },
  { featureType: 'administrative.country', elementType: 'labels.text.fill', stylers: [{ color: '#9aa0a6' }] },
  { featureType: 'administrative.land_parcel', stylers: [{ visibility: 'off' }] },
  { featureType: 'administrative.locality', elementType: 'labels.text.fill', stylers: [{ color: '#cbd5e1' }] },
  { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi', elementType: 'geometry', stylers: [{ color: '#1e293b' }] },
  { featureType: 'poi.business', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#162032' }] },
  { featureType: 'poi.park', elementType: 'labels.text.fill', stylers: [{ color: '#4b6584' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#1e293b' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#212a37' }] },
  { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#9ca3af' }] },
  { featureType: 'road', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#2d3748' }] },
  { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#1e293b' }] },
  { featureType: 'road.highway', elementType: 'labels.text.fill', stylers: [{ color: '#cbd5e1' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#0a1628' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#515c6d' }] },
  { featureType: 'water', elementType: 'labels.text.stroke', stylers: [{ color: '#17263c' }] },
  { featureType: 'landscape', elementType: 'geometry', stylers: [{ color: '#131c2e' }] },
];

function mapAppearance(dark: boolean): { styles: google.maps.MapTypeStyle[]; backgroundColor: string } {
  return dark
    ? { styles: DARK_MAP_STYLE, backgroundColor: DARK_MAP_BACKGROUND }
    : { styles: MAP_STYLE, backgroundColor: LIGHT_MAP_BACKGROUND };
}

function placedPins(homes: ApplicationHomePin[]): PlacedPin[] {
  const groups = new Map<string, { labels: string[]; pickLabels: string[]; position: google.maps.LatLngLiteral }>();
  for (const home of homes) {
    const key = mapPinGroupKey(home);
    const group = groups.get(key) ?? {
      labels: [],
      pickLabels: [],
      position: { lat: home.latitude, lng: home.longitude },
    };
    group.labels.push(home.label);
    group.pickLabels.push(home.pickLabel);
    groups.set(key, group);
  }
  return [...groups.entries()].map(([key, group]) => ({
    key,
    labels: group.labels,
    pickLabels: group.pickLabels,
    position: group.position,
  }));
}

function escapeSvgText(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function pillWidthForLabel(text: string): number {
  return Math.max(76, Math.min(220, text.length * 7 + 28));
}

function pinIcon(pickLabels: string[]): google.maps.Icon {
  const shown = summarizeMapPinLabels(pickLabels);
  const gap = 6;
  const widths = shown.map(pillWidthForLabel);
  const width = widths.reduce((sum, w) => sum + w, 0) + Math.max(0, shown.length - 1) * gap;
  let x = 0;
  const bodies = shown.map((text, index) => {
    const pillWidth = widths[index] ?? 76;
    const body = `<rect x="${x}" y="0" width="${pillWidth}" height="28" rx="14" fill="${PIN_COLOR}"/>
      <text x="${x + pillWidth / 2}" y="18.5" text-anchor="middle" fill="#ffffff" font-family="Arial, sans-serif" font-size="12" font-weight="700">${escapeSvgText(text)}</text>`;
    x += pillWidth + gap;
    return body;
  }).join('');
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="28" viewBox="0 0 ${width} 28">${bodies}</svg>`;
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new google.maps.Size(width, 28),
    anchor: new google.maps.Point(width / 2, 14),
  };
}

async function waitForMapContainer(ref: RefObject<HTMLDivElement | null>): Promise<HTMLDivElement | null> {
  for (let attempt = 0; attempt < 40; attempt += 1) {
    if (ref.current) return ref.current;
    await new Promise<void>((resolve) => {
      window.requestAnimationFrame(() => resolve());
    });
  }
  return ref.current;
}

function areaPositions(area: MapPoint[]): google.maps.LatLngLiteral[] {
  return area.flatMap((point) => (
    point.latitude == null || point.longitude == null ? [] : [{ lat: point.latitude, lng: point.longitude }]
  ));
}

function frameDistrict(
  map: google.maps.Map,
  points: google.maps.LatLngLiteral[],
) {
  if (points.length === 0) {
    map.setCenter(KINGSTON);
    map.setZoom(14);
    return;
  }
  if (points.length === 1) {
    map.setCenter(points[0]);
    map.setZoom(14);
    return;
  }
  const bounds = new google.maps.LatLngBounds();
  points.forEach((point) => bounds.extend(point));
  map.fitBounds(bounds, 48);
  google.maps.event.addListenerOnce(map, 'idle', () => {
    const zoom = map.getZoom();
    if (zoom == null) return;
    if (zoom > 15) map.setZoom(15);
    if (zoom < 13) map.setZoom(13);
  });
}

function mapFallbackMessage(state: MapLoadState): string {
  if (state === 'no-key') {
    return 'Map preview is not available in this environment.';
  }
  return 'We couldn’t load the map. Refresh the page and try again.';
}

export default function ApplicationHomesMap({ homes, area = [], fillHeight = false }: ApplicationHomesMapProps) {
  const { resolvedTheme } = useTenantAppearance();
  const dark = resolvedTheme === 'dark';
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<google.maps.Map | null>(null);
  const markers = useRef<google.maps.Marker[]>([]);
  const [loadState, setLoadState] = useState<MapLoadState>('loading');
  const pins = placedPins(homes);
  const district = areaPositions(area);
  const homesMissingCoords = homes.length > 0 && pins.length === 0;
  const pinKey = pins.map((pin) => `${pin.key}:${pin.pickLabels.join(',')}`).join('|');
  const areaKey = district.map((point) => `${point.lat.toFixed(5)},${point.lng.toFixed(5)}`).join('|');
  const appearance = mapAppearance(dark);

  useEffect(() => {
    let cancelled = false;
    setLoadState('loading');

    void (async () => {
      const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
      if (cancelled) return;
      if (!apiKey) {
        setLoadState('no-key');
        return;
      }

      const container = await waitForMapContainer(mapRef);
      if (cancelled) return;
      if (!container) {
        setLoadState('failed');
        return;
      }

      const ready = await tenantGoogleMapsLoader.isReady('maps');
      if (cancelled) return;
      if (!ready) {
        setLoadState('failed');
        return;
      }

      try {
        const map = !mapInstance.current || mapInstance.current.getDiv() !== container
          ? new google.maps.Map(container, {
            center: KINGSTON,
            zoom: 14,
            disableDefaultUI: true,
            zoomControl: true,
            gestureHandling: 'cooperative',
            clickableIcons: false,
            backgroundColor: appearance.backgroundColor,
            styles: appearance.styles,
          })
          : mapInstance.current;
        map.setOptions({ styles: appearance.styles, backgroundColor: appearance.backgroundColor });
        mapInstance.current = map;
        google.maps.event.trigger(map, 'resize');

        markers.current.forEach((marker) => marker.setMap(null));
        markers.current = pins.map((pin) => new google.maps.Marker({
          map,
          position: pin.position,
          title: pin.labels.join(', '),
          icon: pinIcon(pin.pickLabels),
        }));

        if (pins.length === 1) {
          map.setCenter(pins[0].position);
          map.setZoom(15);
        } else if (pins.length > 1) {
          const bounds = new google.maps.LatLngBounds();
          pins.forEach((pin) => bounds.extend(pin.position));
          map.fitBounds(bounds, 64);
          google.maps.event.addListenerOnce(map, 'idle', () => {
            const zoom = map.getZoom();
            if (zoom != null && zoom > 15) map.setZoom(15);
          });
        } else if (district.length > 0) {
          frameDistrict(map, district);
        } else {
          map.setCenter(KINGSTON);
          map.setZoom(14);
        }

        if (!cancelled) setLoadState('ready');
      } catch {
        if (!cancelled) setLoadState('failed');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [pinKey, areaKey, dark]);

  useEffect(() => () => {
    markers.current.forEach((marker) => marker.setMap(null));
    markers.current = [];
  }, []);

  useEffect(() => {
    const node = mapRef.current;
    if (!node) return;
    const observer = new ResizeObserver(() => {
      const map = mapInstance.current;
      if (!map || !window.google?.maps?.event) return;
      const center = map.getCenter();
      google.maps.event.trigger(map, 'resize');
      if (center) map.setCenter(center);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [pinKey, areaKey]);

  const shellClass = fillHeight
    ? 'absolute inset-0 min-h-64 w-full'
    : 'relative h-full min-h-64 w-full';

  return (
    <div className={shellClass}>
      {loadState === 'loading' ? (
        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-xl bg-[#f3f0ea]/90 text-sm text-gray-500 dark:bg-[#0f172a]/90 dark:text-gray-400">
          Loading map…
        </div>
      ) : null}
      {loadState === 'no-key' || loadState === 'failed' ? (
        <div className="absolute inset-0 z-20 flex items-center justify-center rounded-xl border border-dashed border-gray-200 bg-gray-50/95 px-4 text-center text-sm text-gray-500 dark:border-white/10 dark:bg-gray-950/95 dark:text-gray-400">
          {mapFallbackMessage(loadState)}
        </div>
      ) : null}
      {loadState === 'ready' && homesMissingCoords ? (
        <div className="pointer-events-none absolute bottom-3 left-3 right-3 z-10 rounded-lg border border-amber-200/80 bg-amber-50/95 px-3 py-2 text-center text-xs text-amber-900 shadow-sm dark:border-amber-500/30 dark:bg-amber-950/90 dark:text-amber-100">
          We don&apos;t have map coordinates for these homes yet. The list above is still correct.
        </div>
      ) : null}
      <div
        ref={mapRef}
        className="h-full min-h-64 w-full overflow-hidden rounded-xl bg-[#f3f0ea] dark:bg-[#0f172a]"
      />
    </div>
  );
}
